import { useState, useLayoutEffect, useEffect, useMemo } from 'react';
import {
    MediumHeightDivider,
    Title
} from '../../theme/Styles';
import { useDispatch } from 'react-redux';
import { UpdateAppSubHeaderTitle } from '../../redux/actions/UiActions';
import {
    Container,
    BreadcrumbWrapper,
    BreadcrumbInner,
    BreadcrumbContent,
    BreadcrumbLink,
    BreadcrumbSeparator,
    BreadcrumbCurrent,
    ToolbarSection,
    SearchBox,
    SearchIconWrap,
    SearchInput,
    UpdatedBadge,
    UpdatedDot,
    ChipsRow,
    Chip,
    ChipSwatch,
    Panel,
    CountBar,
    DocList,
    DocItem,
    DocIcon,
    DocBody,
    DocEyebrow,
    DocTitle,
    DocMeta,
    MetaItem,
    FormatTag,
    DocAside,
    CategoryTag,
    ActionsRow,
    EmptyState,
    PaginationBar,
    RowsPerPageGroup,
    RowsPerPageLabel,
    RowsPerPageSelect,
    ResultsCount,
    PagesGroup,
    INK,
    INK_SOFT,
    INK_FAINT,
    ACCENT,
    ACCENT_HOVER,
    BORDER_STRONG
} from './styles/BaseLegalStyles';
import { downloadFileWithName } from '../../utilities/functions/DownloadUtil';
import {
    Typography,
    Box,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { getDocumentsFromTransparencia } from '../../api/GeneralQueries';
import CenterLoading from '../../components/CenterLoading/CenterLoading';
import FONTS_FAMILY from '../../theme/FontsFamily';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LastPageIcon from '@mui/icons-material/LastPage';

// Paleta de categorías legales en tonos azules e institucionales (sin verde)
const CATEGORY_VISUALS = {
    leyes: { edge: '#0F2350', ink: '#0F2350', bg: '#E3F4F6' },
    decretos: { edge: '#0063bc', ink: '#0063bc', bg: '#E7ECF6' },
    resoluciones: { edge: '#0B8296', ink: '#0B8296', bg: '#E3F4F6' },
    constitucion: { edge: '#0F2350', ink: '#0F2350', bg: '#E7ECF6' },
    normativas: { edge: '#0063bc', ink: '#0063bc', bg: '#E3F4F6' },
};
const DEFAULT_VISUAL = { edge: '#57697F', ink: '#57697F', bg: '#F1F6F7' };

const getCategoryVisual = (categoryId) => {
    const key = String(categoryId || '').toLowerCase();
    return CATEGORY_VISUALS[key] || DEFAULT_VISUAL;
};

// Helper to get category icon
const getCategoryIcon = (sub) => {
    const type = sub?.toLowerCase();
    if (type === 'leyes' || type === 'constitucion') return <DescriptionIcon sx={{ fontSize: 22 }} />;
    if (type === 'decretos') return <AccountBalanceIcon sx={{ fontSize: 22 }} />;
    if (type === 'resoluciones') return <AssignmentIcon sx={{ fontSize: 22 }} />;
    return <GavelIcon sx={{ fontSize: 22 }} />;
};

function BaseLegal() {
    const dispatch = useDispatch();
    const history = useHistory();

    const [typeSelectedOption, setTypeSelectedOption] = useState('all');
    const [filterString, setFilterString] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewingUrl, setViewingUrl] = useState('');
    const [viewingTitle, setViewingTitle] = useState('');

    const { data: documentsData, isLoading: documentsLoading, isError, error } = useQuery(
        ['baseLegalDocuments'],
        () => getDocumentsFromTransparencia(),
        {
            retry: 2,
            staleTime: 5 * 60 * 1000, // 5 minutes
        }
    );

    const handleViewDocument = (title, url) => {
        setViewingTitle(title);
        setViewingUrl(url);
        setViewerOpen(true);
    };

    // Helper to format subcategory name nicely
    const formatSubcategory = (sub) => {
        const mapping = {
            'constitucion': 'Constitución',
            'leyes': 'Leyes',
            'decretos': 'Decretos',
            'resoluciones': 'Resoluciones',
            'normativas': 'Normativas'
        };
        return mapping[sub] || (sub ? sub.charAt(0).toUpperCase() + sub.slice(1) : 'Documento');
    };

    // Helper to format date cleanly
    const formatDate = (dateStr) => {
        if (!dateStr) return '---';
        try {
            const date = new Date(dateStr);
            const options = { day: '2-digit', month: 'short', year: 'numeric' };
            const formatted = date.toLocaleDateString('es-ES', options);
            // Remove dot and capitalize first letter
            return formatted.replace(/\./g, '').replace(/^[a-z]/, L => L.toUpperCase());
        } catch (e) {
            return '---';
        }
    };

    // Helper para convertir bytes en formato legible (KB, MB)
    const formatBytes = (bytes) => {
        if (!bytes || isNaN(bytes)) return null;
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    // Flatten all documents with their category label
    const masterList = useMemo(() => {
        if (!documentsData) return [];

        let rawItems = [];
        const sourceData = Array.isArray(documentsData)
            ? documentsData
            : (documentsData?.data ?? []);

        if (Array.isArray(sourceData)) {
            sourceData.forEach(catOrDoc => {
                const innerDocs = catOrDoc.documents || catOrDoc.docs || catOrDoc.items || catOrDoc.files || catOrDoc.documentos;
                if (Array.isArray(innerDocs) && innerDocs.length > 0) {
                    innerDocs.forEach(doc => {
                        rawItems.push({
                            ...doc,
                            category_name: catOrDoc.name || catOrDoc.title || catOrDoc.category || 'General',
                            category_id: catOrDoc.id || catOrDoc.slug || 'all'
                        });
                    });
                } else {
                    rawItems.push(catOrDoc);
                }
            });
        }

        return rawItems.map((item, idx) => ({
            id: item.id || idx,
            documentTitle: item.title || item.name || item.documentTitle || 'Documento Legal',
            categoryName: item.category_name || item.documentType || formatSubcategory(item.subcategory) || 'General',
            categoryId: item.category_id || item.subcategory || item.documentType || 'all',
            fileSize: formatBytes(item.file_size || item.size || item.filesize),
            documentURL: item.documentURL || item.url || item.file_url || (item.file_path ? `https://mitur.gob.do/uploads/${item.file_path}` : ''),
            submissionDate: item.date || item.submission_date || item.created_at || item.post_date
        }));
    }, [documentsData]);

    const selectOptions = useMemo(() => {
        if (!documentsData) return [{ label: 'Todas', value: 'all' }];

        let catOptions = [];
        const sourceData = Array.isArray(documentsData)
            ? documentsData
            : (documentsData?.data ?? []);

        if (Array.isArray(sourceData)) {
            sourceData.forEach(cat => {
                const hasDocs = Array.isArray(cat.documents || cat.docs || cat.items || cat.files || cat.documentos);
                if (hasDocs && (cat.name || cat.title)) {
                    const label = cat.name || cat.title;
                    const value = cat.id || cat.slug || label;
                    if (!catOptions.some(opt => opt.value === value)) {
                        catOptions.push({ label: formatSubcategory(label), value });
                    }
                }
            });
        }

        if (catOptions.length > 0) {
            return [{ label: 'Todas', value: 'all' }, ...catOptions];
        }

        const uniqueSubs = Array.from(new Set(masterList.map(item => item.categoryId).filter(Boolean)));
        const options = uniqueSubs.map(sub => ({
            label: formatSubcategory(sub),
            value: sub,
        }));
        return [{ label: 'Todas', value: 'all' }, ...options];
    }, [documentsData, masterList]);

    const filteredItems = useMemo(() => {
        const queryStr = String(filterString || '').toLowerCase();
        const result = masterList.filter(item => {
            const matchesCategory = !typeSelectedOption || typeSelectedOption === 'all' || String(item.categoryId) === String(typeSelectedOption);
            const docTitleStr = String(item?.documentTitle || item?.title || item?.name || '').toLowerCase();
            const matchesText = docTitleStr.includes(queryStr);
            return matchesCategory && matchesText;
        });
        return result;
    }, [masterList, typeSelectedOption, filterString]);

    const paginatedItems = useMemo(() => {
        return filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredItems, page, rowsPerPage]);

    useLayoutEffect(() => {
        // We remove the UpdateAppSubHeaderTitle dispatch to hide the blue banner
        dispatch(UpdateAppSubHeaderTitle(''));
    }, [dispatch]);

    useEffect(() => {
        setPage(0);
    }, [typeSelectedOption, filterString]);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownload = (url, name) => {
        downloadFileWithName(url, name);
    };

    const totalPages = Math.max(1, Math.ceil(filteredItems.length / rowsPerPage));

    const pageNumbers = useMemo(() => {
        const maxPagesToShow = 3;
        let start = Math.max(0, page - 1);
        let end = Math.min(totalPages - 1, page + 1);

        if (end - start + 1 < maxPagesToShow && totalPages > maxPagesToShow) {
            if (start === 0) {
                end = maxPagesToShow - 1;
            } else if (end === totalPages - 1) {
                start = totalPages - maxPagesToShow;
            }
        }

        const pages = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }, [page, totalPages]);

    if (documentsLoading) return <CenterLoading />;

    if (isError) {
        return (
            <Container>
                <Box sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="h6" color="error" sx={{ fontFamily: FONTS_FAMILY.bold.body, mb: 1 }}>
                        Error al cargar los documentos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONTS_FAMILY.regular.body }}>
                        {error?.message || 'No se pudo conectar con la API de transparencia.'}
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container>
            <BreadcrumbWrapper>
                <BreadcrumbInner>
                    <BreadcrumbContent>
                        <BreadcrumbLink onClick={() => history.push('/')}>Inicio</BreadcrumbLink>
                        <BreadcrumbSeparator>/</BreadcrumbSeparator>
                        <BreadcrumbCurrent>Base Legal</BreadcrumbCurrent>
                    </BreadcrumbContent>
                </BreadcrumbInner>
            </BreadcrumbWrapper>

            <Box sx={{ mb: 4 }}>
                <Title sx={{ mb: 1.5, color: '#0F2350', fontWeight: 900, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                    Base Legal y Normativas
                </Title>
                <Typography variant="body1" sx={{ color: '#57697F', fontSize: '1.05rem', fontFamily: FONTS_FAMILY.regular.body, maxWidth: 720 }}>
                    Consulta las leyes, decretos, resoluciones y reglamentos que rigen el sector turístico nacional.
                </Typography>
            </Box>

            <ToolbarSection>
                <SearchBox>
                    <SearchIconWrap>
                        <SearchIcon sx={{ fontSize: 20 }} />
                    </SearchIconWrap>
                    <SearchInput
                        type="search"
                        placeholder="Buscar por nombre de documento..."
                        aria-label="Buscar documentos"
                        value={filterString}
                        onChange={(e) => setFilterString(e.target.value)}
                    />
                </SearchBox>
            </ToolbarSection>

            <ChipsRow role="group" aria-label="Filtrar por tipo de norma">
                {selectOptions.map((option) => {
                    const isAll = option.value === 'all';
                    const visual = isAll ? null : getCategoryVisual(option.value);
                    const active = typeSelectedOption === option.value;
                    return (
                        <Chip
                            key={option.value}
                            type="button"
                            $active={active}
                            aria-pressed={active}
                            onClick={() => setTypeSelectedOption(option.value)}
                        >
                            {option.label}
                        </Chip>
                    );
                })}
            </ChipsRow>

            <Panel>
                <CountBar>
                    <span>
                        Mostrando <b>{paginatedItems.length}</b> de <b>{filteredItems.length}</b> documentos
                    </span>
                </CountBar>

                {paginatedItems.length > 0 ? (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px', border: '1px solid #E1E9EC', overflow: 'hidden' }}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead sx={{ backgroundColor: '#0F2350' }}>
                                <TableRow>
                                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '13px', py: 1.8 }}>Categoría</TableCell>
                                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '13px', py: 1.8 }}>Nombre del Documento</TableCell>
                                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '13px', py: 1.8 }}>Publicado</TableCell>
                                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '13px', py: 1.8 }}>Formato / Tamaño</TableCell>
                                    <TableCell align="right" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '13px', py: 1.8, pr: 3 }}>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedItems.map((row, index) => {
                                    const isEven = index % 2 === 0;
                                    const rowBg = isEven ? '#F0F4FF' : '#F8FAFC'; // Azul claro y gris claro intercalados
                                    const visual = getCategoryVisual(row.categoryId);

                                    return (
                                        <TableRow 
                                            key={row.id ?? index}
                                            sx={{ 
                                                backgroundColor: rowBg, 
                                                '&:hover': { backgroundColor: '#E3F4F6' },
                                                transition: 'background-color 0.2s ease'
                                            }}
                                        >
                                            <TableCell sx={{ py: 1.8 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                                    <Box className="doc-icon-box" sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '10px',
                                                        backgroundColor: visual.bg,
                                                        color: visual.ink,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        {getCategoryIcon(row.categoryId)}
                                                    </Box>
                                                    <Typography sx={{ fontWeight: 700, fontSize: '12.5px', color: '#0F2350', textTransform: 'uppercase' }}>
                                                        {row.categoryName}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.8 }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: '14.5px', color: '#0F2350', lineHeight: 1.4 }}>
                                                    {row.documentTitle}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.8, whiteSpace: 'nowrap' }}>
                                                <Typography sx={{ fontSize: '13px', color: '#57697F', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                    <CalendarTodayIcon sx={{ fontSize: 14, color: '#8A99AC' }} />
                                                    {formatDate(row.submissionDate)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.8, whiteSpace: 'nowrap' }}>
                                                <Box className="format-badge" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.4, borderRadius: '6px', backgroundColor: '#FFFFFF', border: '1px solid #CFDCE0' }}>
                                                    <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#0F2350' }}>PDF</Typography>
                                                    {row.fileSize && <Typography sx={{ fontSize: '12px', color: '#57697F' }}>{row.fileSize}</Typography>}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.8, pr: 3 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                    <Button
                                                        onClick={() => handleViewDocument(row.documentTitle, row.documentURL)}
                                                        variant="outlined"
                                                        disabled={!row.documentURL}
                                                        startIcon={<VisibilityIcon sx={{ fontSize: '15px !important' }} />}
                                                        sx={{
                                                            height: '36px',
                                                            px: 2,
                                                            borderRadius: '8px',
                                                            borderColor: '#CFDCE0',
                                                            color: '#0F2350',
                                                            fontFamily: FONTS_FAMILY.bold.body,
                                                            fontSize: '12.5px',
                                                            fontWeight: 700,
                                                            textTransform: 'none',
                                                            boxShadow: 'none',
                                                            whiteSpace: 'nowrap',
                                                            '&:hover': {
                                                                bgcolor: '#FFFFFF',
                                                                borderColor: '#0063bc',
                                                                color: '#0063bc'
                                                            }
                                                        }}
                                                    >
                                                        Ver
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDownload(row.documentURL, row.documentTitle)}
                                                        variant="contained"
                                                        disabled={!row.documentURL}
                                                        startIcon={<FileDownloadIcon sx={{ fontSize: '15px !important' }} />}
                                                        sx={{
                                                            height: '36px',
                                                            px: 2,
                                                            borderRadius: '8px',
                                                            bgcolor: '#0F2350',
                                                            color: '#ffffff',
                                                            fontFamily: FONTS_FAMILY.bold.body,
                                                            fontSize: '12.5px',
                                                            fontWeight: 700,
                                                            textTransform: 'none',
                                                            boxShadow: 'none',
                                                            whiteSpace: 'nowrap',
                                                            '&:hover': {
                                                                bgcolor: '#EA361A'
                                                            }
                                                        }}
                                                    >
                                                        Descargar
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <EmptyState>
                        <Typography sx={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '17px', color: INK, mb: 0.5 }}>
                            Sin resultados
                        </Typography>
                        <Typography sx={{ fontFamily: FONTS_FAMILY.regular.body, fontSize: '13.5px', color: INK_FAINT }}>
                            Ajusta la búsqueda o prueba con otro tipo de norma.
                        </Typography>
                    </EmptyState>
                )}

                <PaginationBar>
                    <RowsPerPageGroup>
                        <RowsPerPageLabel>
                            Mostrar:
                            <RowsPerPageSelect value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </RowsPerPageSelect>
                        </RowsPerPageLabel>
                        <ResultsCount>
                            {filteredItems.length === 0 ? 0 : page * rowsPerPage + 1} a {Math.min((page + 1) * rowsPerPage, filteredItems.length)} de <b>{filteredItems.length}</b> resultados
                        </ResultsCount>
                    </RowsPerPageGroup>

                    <PagesGroup>
                        <IconButton
                            disabled={page === 0}
                            onClick={() => setPage(0)}
                            sx={{
                                width: '38px',
                                height: '38px',
                                borderRadius: '8px',
                                border: `1px solid ${BORDER_STRONG}`,
                                color: INK_SOFT,
                                transition: 'all 0.2s',
                                '&:hover:not(:disabled)': {
                                    bgcolor: INK,
                                    color: '#ffffff',
                                    borderColor: INK
                                }
                            }}
                        >
                            <FirstPageIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                            disabled={page === 0}
                            onClick={() => setPage(page - 1)}
                            sx={{
                                width: '38px',
                                height: '38px',
                                borderRadius: '8px',
                                border: `1px solid ${BORDER_STRONG}`,
                                color: INK_SOFT,
                                transition: 'all 0.2s',
                                '&:hover:not(:disabled)': {
                                    bgcolor: INK,
                                    color: '#ffffff',
                                    borderColor: INK
                                }
                            }}
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>

                        {pageNumbers.map((pageNum) => {
                            const isActive = page === pageNum;
                            return (
                                <Button
                                    key={pageNum}
                                    onClick={() => setPage(pageNum)}
                                    sx={{
                                        minWidth: '38px',
                                        width: '38px',
                                        height: '38px',
                                        borderRadius: '8px',
                                        bgcolor: isActive ? INK : 'transparent',
                                        color: isActive ? '#ffffff' : INK_SOFT,
                                        border: isActive ? 'none' : `1px solid ${BORDER_STRONG}`,
                                        fontFamily: FONTS_FAMILY.bold.body,
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: INK,
                                            color: '#ffffff',
                                            borderColor: INK
                                        }
                                    }}
                                >
                                    {pageNum + 1}
                                </Button>
                            );
                        })}

                        <IconButton
                            disabled={page >= totalPages - 1 || filteredItems.length === 0}
                            onClick={() => setPage(page + 1)}
                            sx={{
                                width: '38px',
                                height: '38px',
                                borderRadius: '8px',
                                border: `1px solid ${BORDER_STRONG}`,
                                color: INK_SOFT,
                                transition: 'all 0.2s',
                                '&:hover:not(:disabled)': {
                                    bgcolor: INK,
                                    color: '#ffffff',
                                    borderColor: INK
                                }
                            }}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                            disabled={page >= totalPages - 1 || filteredItems.length === 0}
                            onClick={() => setPage(totalPages - 1)}
                            sx={{
                                width: '38px',
                                height: '38px',
                                borderRadius: '8px',
                                border: `1px solid ${BORDER_STRONG}`,
                                color: INK_SOFT,
                                transition: 'all 0.2s',
                                '&:hover:not(:disabled)': {
                                    bgcolor: INK,
                                    color: '#ffffff',
                                    borderColor: INK
                                }
                            }}
                        >
                            <LastPageIcon fontSize="small" />
                        </IconButton>
                    </PagesGroup>
                </PaginationBar>
            </Panel>

            <Dialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                maxWidth="lg"
                fullWidth={true}
                PaperProps={{
                    style: {
                        borderRadius: '16px',
                        height: '85vh',
                        display: 'flex',
                        flexDirection: 'column',
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #e2e8f0' }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: INK,
                            fontFamily: FONTS_FAMILY.medium.title,
                            fontSize: '1.1rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            pr: 2,
                            flex: 1
                        }}
                    >
                        {viewingTitle}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Descargar Documento">
                            <IconButton
                                onClick={() => window.open(viewingUrl, '_blank')}
                                size="small"
                                sx={{ color: ACCENT }}
                            >
                                <FileDownloadIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={() => setViewerOpen(false)} size="small" sx={{ color: INK_SOFT }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Box sx={{ flex: 1, p: 0, bgcolor: '#f8fafc', overflow: 'hidden' }}>
                    {viewingUrl ? (
                        <iframe
                            src={viewingUrl}
                            title={viewingTitle}
                            width="100%"
                            height="100%"
                            style={{ border: 'none', display: 'block' }}
                        />
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography variant="body1" color="text.secondary">
                                Cargando documento...
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Dialog>

            <MediumHeightDivider />
        </Container>
    );
}

export default BaseLegal;
